import {Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges} from '@angular/core';
import {Subscription} from 'rxjs';
import {FilterCriteria} from 'features/core/shared/filter/api/filter.criteria';
import {CpConfigChildTab} from '../../../../../interface/cp-config-child-tab.interface';
import {
    CpProvisionTypeComponentOption,
    CpProvisionTypeLookupService
} from '../../../../../service/ql/cp-provision-type-lookup.service';

/**
 * navItem "Matrix-Zuordnung" of the ProvisionType object component (04-frontend.md §6, entry 8).
 *
 * The table itself is the generic {@link CpProvisionTypeChildTableComponent} on
 * `cp.ProvisionTypeMatrixAssignment` — same descriptor mechanism, same FK pre-fill, same fixed
 * column block and read-only condition preview as the sibling assignment tables. This component adds ONE thing on
 * top, and it is the one 04 §6 asks for by name: the pre-filter on the commission component
 * ("Vorfilter auf die gewählte Provisionsart als Bedien-Hilfe").
 *
 * ## Why the pre-filter matters here and nowhere else
 * K-10 is the only one of the four assignment entities that gained a MANDATORY
 * `provisionTypeComponent` FK with E2. Since the engine resolves one matrix PER COMMISSION
 * COMPONENT, the rule set of a provision type with three components is three interleaved rule sets
 * in one table. Reading the priority order of a single component out of that mixture is what the
 * pre-filter fixes; it is an operating aid on the READ side only and changes nothing about how the
 * rows are stored or created.
 *
 * "Alle Provisionsarten" (`null`) is the default, so the tab still opens on the complete rule set.
 *
 * ## Widget identity across filter changes (CONFIG-011)
 * Switching the pre-filter must not look like a different table: the child component keeps its
 * alias `config.cp.ProvisionTypeMatrixAssignment.table` for every filter value, so column widths
 * and sorting survive; the column set is fixed, so it does not shift while the user pages through
 * components.
 */
@Component({
    selector: 'cp-provision-type-matrix-assignment',
    templateUrl: './cp-provision-type-matrix-assignment.component.html',
    styleUrls: ['./cp-provision-type-matrix-assignment.component.scss'],
    standalone: false
})
export class CpProvisionTypeMatrixAssignmentComponent implements OnInit, OnChanges, OnDestroy {

    busy: boolean;
    initialized: boolean;

    @Input() provisionTypeId: number;

    /** Descriptor of the matrix-assignment child list, from `CpProvisionTypeBaseComponent`. */
    @Input() child: CpConfigChildTab;

    /** "Im Bedingungssatz öffnen" of the condition preview, forwarded to the object view. */
    @Output() onJumpToConditionSet = new EventEmitter<number>();

    /** Commission components of this provision type — the options of the pre-filter. */
    componentOptions: CpProvisionTypeComponentOption[] = [];

    /** Selected commission component; `null` = no pre-filter, the complete rule set is shown. */
    selectedComponentId: number | null = null;

    /** Handed to the child table; `null` while nothing is pre-filtered. */
    additionalFilters: FilterCriteria[] = null;

    private componentSubscription: Subscription;

    constructor(private lookupService: CpProvisionTypeLookupService) {
    }

    ngOnInit(): void {
        this.initComponent();
    }

    initComponent(): void {
        this.refreshComponent();
    }

    refreshComponent(): void {

        this.cancelComponentLoad();

        this.selectedComponentId = null;
        this.additionalFilters = null;
        this.componentOptions = [];

        if (!this.provisionTypeId) {
            this.initialized = true;
            return;
        }

        this.busy = true;
        this.componentSubscription = this.lookupService
            .getComponents(this.provisionTypeId)
            .subscribe(options => {
                this.componentOptions = options ?? [];
                this.busy = false;
                this.initialized = true;
            });

        this.initialized = true;
    }

    /**
     * Apply the pre-filter. `null` clears it and returns the complete rule set of the provision
     * type — the state the tab opens in.
     */
    onComponentChange(componentId: number | null): void {
        this.selectedComponentId = componentId ?? null;
        this.additionalFilters = this.selectedComponentId
            ? [FilterCriteria.create(
                'e.provisionTypeComponentId', FilterCriteria.cOperatorEqual, this.selectedComponentId)]
            : null;
    }

    private cancelComponentLoad(): void {
        if (this.componentSubscription) {
            this.componentSubscription.unsubscribe();
            this.componentSubscription = null;
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (!this.initialized) {
            return;
        }
        if (changes['provisionTypeId'] || changes['child']) {
            this.refreshComponent();
        }
    }

    ngOnDestroy(): void {
        this.cancelComponentLoad();
    }

}
